import React, { useState } from 'react';
import {
  Typography,
  IconButton,
  Grid,
  Paper,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Pagination,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import GridViewIcon from '@mui/icons-material/GridView';
import ViewListIcon from '@mui/icons-material/ViewList';
import AddIcon from '@mui/icons-material/Add';
import { useProperty } from '../../../context/PropertyContext';
import PostModal from '../../../components/modals/PostModal';
import Swal from 'sweetalert2';
import { useNavigate } from 'react-router-dom';
import { PropertyListing } from '../../../types/Property';
import CustomAppBar from '../../../components/shared/CustomAppBar';

const UserPostsPage = () => {
  const { userPropertyPosts, deleteProperty, handleSetSelectedProperty } = useProperty();
  const navigate = useNavigate();
  const [view, setView] = useState('table');
  const [selectedPost, setSelectedPost] = useState<PropertyListing | null>(null);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [page, setPage] = useState(1); // Estado para la página actual
  const postsPerPage = 8; // Número de publicaciones por página

  const toggleView = () => {
    setView((prevView) => (prevView === 'table' ? 'card' : 'table'));
  };

  const handleRowClick = (propertyPost: PropertyListing) => {
    setSelectedPost(propertyPost);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedPost(null);
  };

  const handleEdit = () => {
    handleSetSelectedProperty(selectedPost);
    navigate(`/properties/edit-property/${selectedPost!.title}`);
    handleCloseModal();
  };

  const handleDelete = async () => {
    handleCloseModal();
    const result = await Swal.fire({
      title: '¿Estás seguro?',
      text: 'Esta acción eliminará la propiedad y todas sus fotos asociadas.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Eliminar',
      cancelButtonText: 'Cancelar',
    });

    if (result.isConfirmed && selectedPost) {
      handleCloseModal();
      try {
        const formData = new FormData();
        formData.append('PropertyId', selectedPost.id_Property.toString());
        const deleteResult = await deleteProperty(formData);
        if (deleteResult) {
          Swal.fire('¡Eliminado!', 'Propiedad eliminada', 'success');
        } else {
          Swal.fire('Error', 'error al eliminar', 'error');
        }
      } catch (error) {
        Swal.fire('Error', 'Ocurrió un error al eliminar la propiedad.', 'error');
      }
    }
  };

  const handleSearchToggle = () => {
    setSearchOpen(!searchOpen);
    setSearchTerm('');
  };

  const handleCreateToggle = () => {
    navigate('/properties/publish-property/overview');
  };

  const filteredPosts = userPropertyPosts.filter((post) => {
    const lowerCaseSearchTerm = searchTerm.toLowerCase();
    return (
      post.title.toLowerCase().includes(lowerCaseSearchTerm) ||
      post.address_Description.toLowerCase().includes(lowerCaseSearchTerm) ||
      post.categoryName.toLowerCase().includes(lowerCaseSearchTerm)
    );
  });

  // Lógica de paginación
  const handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
    setPage(value);
  };

  const indexOfLastPost = page * postsPerPage;
  const indexOfFirstPost = indexOfLastPost - postsPerPage;
  const currentPosts = filteredPosts.slice(indexOfFirstPost, indexOfLastPost);

  return (
    <Box>
      <CustomAppBar/>
      <Box sx={{ mt: '10vh', p: 6 }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            m: 2,
          }}
        >
          <Typography variant="h5" fontWeight={'bold'}>
            Mis Publicaciones
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', height: 5 }}>
            {searchOpen && (
              <TextField
                variant="outlined"
                placeholder="Buscar..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                sx={{
                  mr: 2,
                  width: '800px',
                  height: '40px',
                  '& .MuiOutlinedInput-root': {
                    '& fieldset': {
                      border: '2px solid',
                      borderColor: 'grey.400',
                    },
                    '&:hover fieldset': {
                      borderColor: 'black',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: 'darkgreen',
                    },
                  },
                }}
              />
            )}
            <IconButton color="inherit" onClick={handleSearchToggle}>
              {searchOpen ? <CloseIcon /> : <SearchIcon />}
            </IconButton>
            <IconButton color="inherit" onClick={toggleView}>
              {view === 'table' ? <GridViewIcon /> : <ViewListIcon />}
            </IconButton>
            <IconButton color="inherit" aria-label="create" onClick={handleCreateToggle}>
              <AddIcon />
            </IconButton>
          </Box>
        </Box>

        <Box sx={{ padding: '16px' }}>
          {filteredPosts.length > 0 ? (
            view === 'table' ? (
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell align="center" style={{ fontWeight: 'bold', fontSize: '1.1rem' }}>Publicación</TableCell>
                      <TableCell align="center" style={{ fontWeight: 'bold', fontSize: '1.1rem' }}>Ubicación</TableCell>
                      <TableCell align="center" style={{ fontWeight: 'bold', fontSize: '1.1rem' }}>Categoría</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {currentPosts.map((post) => (
                      <TableRow
                        key={post.id_Property}
                        onClick={() => handleRowClick(post)}
                        sx={{
                          cursor: 'pointer',
                          '&:hover': { backgroundColor: '#f5f5f5' },
                        }}
                      >
                        <TableCell align="center" style={{ fontSize: '1rem' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <img src={post.photos[0]} alt={post.title} style={{ width: '50px', height: '50px', marginRight: '8px' }} />
                            {post.title}
                          </Box>
                        </TableCell>
                        <TableCell align="center" style={{ fontSize: '1rem' }}>{post.address_Description}</TableCell>
                        <TableCell align="center" style={{ fontSize: '1rem' }}>{post.categoryName}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Grid container spacing={4} justifyContent="center">
                {currentPosts.map((post) => (
                  <Grid item xs={12} sm={6} md={4} lg={3} key={post.id_Property}>
                    <Paper
                      elevation={3}
                      sx={{
                        cursor: 'pointer',
                        overflow: 'hidden',
                        borderRadius: 2,
                        '&:hover': {
                          boxShadow: 6,
                        },
                      }}
                      onClick={() => handleRowClick(post)}
                    >
                      <img
                        src={post.photos[0]}
                        alt={post.title}
                        style={{
                          width: '100%',
                          height: '200px',
                          objectFit: 'cover',
                        }}
                      />
                      <Box sx={{ p: 2 }}>
                        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                          {post.title}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {post.address_Description}
                        </Typography>
                        <Typography variant="body2" color="text.primary" sx={{ mt: 1 }}>
                          {post.categoryName}
                        </Typography>
                      </Box>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            )
          ) : (
            <Typography variant="body1" align="center" color="text.secondary">
              No se encontraron publicaciones.
            </Typography>
          )}

          {/* Paginación */}
          {filteredPosts.length > postsPerPage && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
              <Pagination
                count={Math.ceil(filteredPosts.length / postsPerPage)}
                page={page}
                onChange={handlePageChange}
                color="primary"
              />
            </Box>
          )}
        </Box>
      </Box>

      {selectedPost && (
        <PostModal
          open={modalOpen}
          onClose={handleCloseModal}
          post={selectedPost}
          onEdit={handleEdit}
          onDelete={handleDelete}
        />
      )}
    </Box>
  );
};

export default UserPostsPage;